import { useState } from 'react'
import { Card } from '@radix-ui/themes'
import TextField from '../common/TextField'
import { useNavigate } from 'react-router-dom'
import { LockKeyhole, AlertCircle } from 'lucide-react'
import walletService from '@/services/wallet/wallet.service'
import { useTranslation } from 'react-i18next'

interface PhraseSecurityDialogProps {
  open: boolean
  onClose: () => void
  onVerified?: (password: string) => void
}

const PhraseSecurityDialog = ({ open, onClose, onVerified }: PhraseSecurityDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleVerify = async () => {
    if (!password.trim()) {
      setError(t('dialogs.password_required'))
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const isValid = await walletService.verifyPassword(password)
      if (isValid) {
        if (onVerified) {
          const verifiedPassword = password
          setPassword('')
          onClose()
          onVerified(verifiedPassword)
          return
        }

        // Guardar contraseña en sesión para permitir desencriptar
        sessionStorage.setItem('react_wallet_password', password)
        setPassword('')
        onClose()
        navigate('/recover')
      } else {
        setError(t('dialogs.wrong_password'))
      }
    } catch (err: any) {
      setError(err?.message || t('dialogs.verify_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setPassword('')
    setError('')
    onClose()
  }

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="shadow-xl bg-card w-full max-w-md justify-center relative">
          <div className="flex flex-col gap-6 p-4 place-self-center">
            <div className="flex flex-col gap-3 text-center items-center">
              <div className="bg-card-e2 rounded-full p-4 w-fit border border-border-base">
                <LockKeyhole className="text-primary w-6 h-6" />
              </div>
              <h2 className="text-text-main text-3xl font-bold">
                {t('dialogs.vault_security')}
              </h2>

              <p className="text-text-secondary text-sm">
                {t('dialogs.vault_security_desc')}
              </p>
            </div>

            {error && (
              <div className="flex gap-2.5 rounded-xl border border-accent bg-accent/10 px-4 py-3 items-center">
                <AlertCircle className="text-accent w-5 h-5 shrink-0" />
                <p className="text-text-main text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="uppercase text-xs font-semibold text-text-secondary tracking-[2.4px] ">
                {t('dialogs.access_password')}
              </label>

              <TextField
                type="password"
                showPasswordToggle
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (error) setError('')
                }}
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-col gap-4">
              <button
                onClick={handleVerify}
                disabled={isLoading}
                className="btn-gradient-fill rounded-full py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <p className="font-bold">
                  {isLoading ? t('dialogs.verifying') : t('dialogs.verify_continue')}
                </p>
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="text-text-secondary text-sm py-2 disabled:opacity-50"
              >
                {t('dialogs.cancel')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default PhraseSecurityDialog

